import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {closeAddressBook, openAddressBook, updateSelectedAddress} from '@web/actions/deliveryInfo'
import {applyAddress, deletePresetMemo, saveAsDefaultAddress} from '@web/actions/deliveryManager'
import {editCustomsAddressBook} from '@web/actions/personalCustomsClearance'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {DELIVERY_MEMO, EntryMethod} from '@web/core/constants'
import {
    changeRecipient,
    changeSaveAsDefaultMemo,
    closeDeliveryModal,
    inputDeliveryMemo,
    inputIndividuallyMemo,
    inputParticularMemo,
    openDeliveryModal,
    selectParticularInput,
    selectUseVirtualPhoneNumber,
    setEntryMethod,
    setPickUpLocation,
    setPickUpLocationAlwaysUse,
    updateDeliveryInstruction,
    ZipCodeValidationStatus,
    getInitialState,
} from '@web/slices/deliveryInfo/slice'

import type {DeliveryInfoReducers, MakeCreateSliceOptions} from './interfaces'
import type {DeliveryInfoState} from '@web/slices/deliveryInfo/slice'

export default (options?: MakeCreateSliceOptions) =>
    createSlice<DeliveryInfoState, DeliveryInfoReducers, 'deliveryInfo', SliceSelectors<DeliveryInfoState>>({
        name: 'deliveryInfo',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(selectUseVirtualPhoneNumber, (state, {payload}) => {
                    state.virtualPhoneNumber.used = payload
                })
                .addCase(selectParticularInput, (state, {payload}) => {
                    state.memo.isParticularInput = payload
                })
                .addCase(inputIndividuallyMemo, (state, {payload}) => {
                    state.memo.individuallyInput = {value: payload, type: DELIVERY_MEMO.WRITTEN}
                })
                .addCase(inputParticularMemo, (state, {payload: [itemId, value]}) => {
                    state.memo.particularInputByItemId[itemId] = {value, type: DELIVERY_MEMO.WRITTEN}
                })
                .addCase(setPickUpLocation, (state, {payload}) => {
                    state.pickup.location.type = payload.type
                    state.pickup.location.content = payload.content
                })
                .addCase(setEntryMethod, (state, {payload}) => {
                    state.pickup.entryMethod.type = payload.type
                    state.pickup.entryMethod.content = payload.content
                })
                .addCase(setPickUpLocationAlwaysUse, (state, {payload}) => {
                    state.pickup.alwaysUses = payload
                })
                .addCase(changeRecipient, (state, {payload}) => {
                    /**
                     * 첫주문 등록이후에는 Scene 정보가 유지되어야한다.
                     * 그 외의 Case에는 ADD로 set
                     * */
                    state.selected.recipient = payload
                })
                .addCase(inputDeliveryMemo, (state, {payload}) => {
                    const {id, data} = payload
                    if (id) {
                        state.memo.particularInputByItemId[id] = data
                        return
                    }
                    state.memo.individuallyInput = data
                })
                .addCase(updateDeliveryInstruction, (state, {payload}) => {
                    const {location, entryMethod, alwaysUses, directReceiveDisabled} = payload

                    state.pickup = {
                        location: {
                            type: location.type,
                            content: location.content,
                        },
                        directReceiveDisabled,
                        entryMethod: {
                            type: entryMethod.type,
                            content: entryMethod.content,
                        },
                        alwaysUses,
                    }
                })
                .addCase(changeSaveAsDefaultMemo, (state, {payload}) => {
                    state.memo.saveAsDefaultMemo = payload
                })
                .addCase(deletePresetMemo.fulfilled, (state, {payload}) => {
                    const list = state.memo.list
                    state.memo.list = list.filter(
                        ({sequenceNumber, template}) => !payload.includes(Number(sequenceNumber)) || template,
                    )
                })
                .addCase(saveAsDefaultAddress.fulfilled, (state) => {
                    state.hasDefaultAddress = true
                })
                .addCase(openAddressBook, (state) => {
                    state.shouldOpenAddressBook = true
                })
                .addCase(closeAddressBook, (state) => {
                    state.shouldOpenAddressBook = false
                })
                .addCase(openDeliveryModal, (state, {payload}) => {
                    state.deliveryModalCase = payload
                })
                .addCase(closeDeliveryModal, (state) => {
                    state.deliveryModalCase = null
                })
                .addCase(applyAddress.fulfilled, (state, {payload}) => {
                    // 모바일 배송지스크린에서 선택 완료 시 스토어 업데이트
                    if (payload?.type === 'deliveryAddress') {
                        const {locationType, locationDescription, entryMethodType, entryDescription, ...addressInfo} =
                            payload.content

                        state.selected.addressInfo = addressInfo
                        state.zipCodeValidation.status = ZipCodeValidationStatus.READY
                        state.zipCodeValidation.validateCase = 'DELIVERY_CHANGE'

                        state.pickup.location = {
                            type: locationType ?? null,
                            content: locationDescription ?? '',
                        }
                        state.pickup.entryMethod = {
                            type: entryMethodType ?? EntryMethod.LOBBY_PW,
                            content: entryDescription ?? '',
                        }
                    }
                })
                .addCase(updateSelectedAddress, (state, {payload}) => {
                    // PC 배송지변경창에서 선택 완료 시 postMessage로 받은 주소 업데이트
                    const {locationType, locationDescription, entryMethodType, entryDescription, ...addressInfo} =
                        payload

                    state.selected.addressInfo = addressInfo
                    state.zipCodeValidation.status = ZipCodeValidationStatus.READY
                    state.zipCodeValidation.validateCase = 'DELIVERY_CHANGE'

                    state.pickup.location = {
                        type: locationType ?? null,
                        content: locationDescription ?? '',
                    }
                    state.pickup.entryMethod = {
                        type: entryMethodType ?? EntryMethod.LOBBY_PW,
                        content: entryDescription ?? '',
                    }
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(
                    isAnyOf(
                        saveAsDefaultAddress.fulfilled, // 전통주 구매 주문서에서 주소 업데이트
                        editCustomsAddressBook.fulfilled,
                    ),
                    (state, {payload}) => {
                        state.selected.addressInfo = payload
                        state.zipCodeValidation.status = ZipCodeValidationStatus.READY
                        state.zipCodeValidation.validateCase = 'DELIVERY_CHANGE'
                    },
                )
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.deliveryInfo ?? state
                })
        },
    })
